<template>
  <div :class="$style.container">
    <template v-if="showSingleRootOrgRow && singleRootOrg">
      <SCAWorkspaceItem
        :title="singleRootOrg.name"
        :subtitle="tenantLabel"
        :icon="tenantIcon"
        :selected="isTenantSelected"
        :expandable="hasChildren"
        :expanded="expanded"
        :level="0"
        @toggle-expand="toggle"
        @click="selectTenant"
      />
      <Transition
        name="expand"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-if="singleRootOrg.children.length > 0 && expanded" :class="$style.container">
          <OrgTreeItem
            v-for="child in singleRootOrg.children"
            :key="child.id"
            :selected-org="selectedOrg"
            :org="child"
            :level="1"
            :expand-all="expandAll"
            @update:selected-org="handleOrgSelect($event)"
          />
        </div>
      </Transition>
    </template>
    <template v-else>
      <SCAWorkspaceItem
        :title="tenant.name"
        :subtitle="tenantLabel"
        :icon="tenantIcon"
        :selected="isTenantSelected"
        :expandable="hasChildren"
        :expanded="expanded"
        :level="0"
        @toggle-expand="toggle"
        @click="selectTenant"
      />
      <Transition
        name="expand"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-if="orgTree.length > 0 && expanded" :class="$style.container">
          <OrgTreeItem
            v-for="org in orgTree"
            :key="org.id"
            :selected-org="selectedOrg"
            :org="org"
            :level="1"
            :expand-all="expandAll"
            @update:selected-org="handleOrgSelect($event)"
          />
        </div>
      </Transition>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { OrganizationWithChildren, TenantWithOrganization } from "@scayla/api-client-fetch";
import { SCAWorkspaceItem } from "@scayla/components";
import { computed } from "vue";

import { useWorkspace } from "@/store/workspace";

import { useTreeItemExpanded } from "./composables/useTreeItemExpanded";
import OrgTreeItem from "./OrgTreeItem.vue";
import { getIcon, getLabel, isRootOrg } from "./tenantOrgaUtils";

const workspace = useWorkspace();

const props = defineProps<{
  tenant: TenantWithOrganization;
  orgTree: OrganizationWithChildren[];
  expandAll?: boolean;
  selectedOrg?: string;
}>();

const emit = defineEmits<{
  close: [];
  "update:selected-org": [orgId: string | undefined, tenantId: string];
}>();

const nodeId = computed(() => props.tenant.id);
const defaultExpanded = computed(() => props.expandAll ?? false);
const { expanded, toggle } = useTreeItemExpanded(nodeId, defaultExpanded);

const hasSingleRootOrg = computed(() => props.orgTree.length === 1);
const singleRootOrg = computed(() => (hasSingleRootOrg.value ? props.orgTree[0] : null));
const showSingleRootOrgRow = computed(
  () => hasSingleRootOrg.value && !!singleRootOrg.value && isRootOrg(props.tenant, singleRootOrg.value),
);
const hasChildren = computed(() => {
  if (showSingleRootOrgRow.value && singleRootOrg.value) {
    return singleRootOrg.value.children.length > 0;
  }
  return props.orgTree.length > 0;
});

const isTenantSelected = computed(() => {
  return workspace.tenantId === props.tenant.id && !props.selectedOrg;
});

const tenantLabel = computed(() => getLabel(true, props.tenant, null));
const tenantIcon = computed(() => getIcon(true, props.tenant, null));

const selectTenant = () => {
  emit("update:selected-org", undefined, props.tenant.id);
  emit("close");
};

const handleOrgSelect = (targetOrgId: string | undefined) => {
  emit("update:selected-org", targetOrgId, props.tenant.id);
  emit("close");
};

// ------------
// Transition hooks
// ------------

const beforeEnter = (el: Element) => {
  (el as HTMLElement).style.height = "0px";
};

const enter = (el: Element) => {
  (el as HTMLElement).style.height = `${(el as HTMLElement).scrollHeight}px`;
};

const afterEnter = (el: Element) => {
  (el as HTMLElement).style.height = "";
};

const beforeLeave = (el: Element) => {
  (el as HTMLElement).style.height = `${(el as HTMLElement).scrollHeight}px`;
};

const leave = (el: Element) => {
  (el as HTMLElement).style.height = "0px";
};

const afterLeave = (el: Element) => {
  (el as HTMLElement).style.height = "";
};
</script>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
}

:global(.expand-enter-active),
:global(.expand-leave-active) {
  transition: height 0.2s linear;
  overflow: hidden;
}

:global(.expand-enter-from),
:global(.expand-leave-to) {
  height: 0;
}
</style>
